import { IPatient } from '../constants/types';
import { decryptSinglePatientData, getInvestigationRawKey } from '../utils';
import { getDecryptPool, type PoolPatientInput } from './decryptPool';
import type { DecryptResponseRow } from './decrypt.worker';
import type { EncryptedField } from './decryptPatientFields';
import Dexie, { type EntityTable } from 'dexie';


const DB_PATIENTS_NAME = 'Patients';
const STORE_PATIENTS_NAME = 'patients';

export let db = new Dexie(DB_PATIENTS_NAME) as Dexie & {
    patients: EntityTable<IPatient, 'uuid'>;
};

// Define the database schema
db.version(2).stores({
    patients: 'uuid, uuidInvestigation, id, dateCreated, personalData.name, personalData.surnames'
});

// Version 31: Removed syncState table (simplified to localStorage)
// Keeping version 31 for compatibility, but syncState table is no longer used
db.version(31).stores({
    patients: 'uuid, uuidInvestigation, id, dateCreated, personalData.name, personalData.surnames'
}).upgrade(async tx => {
    console.log('📦 Database at v31: syncState moved to localStorage for simplicity');
});

export const deleteAllPatientsFromInvestigation = async (uuidInvestigation: string): Promise<void> => {
    await db.patients.where('uuidInvestigation').equals(uuidInvestigation).delete();
};

export const deletePatient = async (uuidPatient: string): Promise<void> => {
    await db.patients.where('uuid').equals(uuidPatient).delete();
};

// The census API returns patients whose `personalData` is still the ENCRYPTED
// field array (not yet the decrypted IPersonalData object). Reflect that real
// wire shape here instead of lying with `IPatient` + `as any` casts.
type RawPatient = Omit<IPatient, 'personalData'> & {
    personalData: EncryptedField[] | null;
};

// What we actually persist to IndexedDB: an IPatient row tagged with the
// owning investigation uuid (the Dexie schema indexes `uuidInvestigation`).
type StoredPatientRow = IPatient & { uuidInvestigation: string };

export const saveListPatients = async (patients: RawPatient[], investigation: any): Promise<void> => {
    console.time("Decrypting Patient Data");
    console.log("Number patients", patients.length);

    // SHER-337: decrypt off the main thread in a Web Worker pool so the UI no
    // longer freezes while a 3000-patient page is decrypted (CryptoJS is
    // synchronous and CPU-bound). Workers have no localStorage, so resolve the
    // investigation raw key HERE on the main thread and pass it in. The early
    // `permissions === 0` path mirrors decryptSinglePatientData (no key, empty
    // personalData) — don't touch localStorage in that case.
    const keyInvestigation =
        investigation.permissions !== 0
            ? getInvestigationRawKey(
                  investigation.uuid,
                  investigation.encryptedKeyUsed,
                  investigation.keyResearcherInvestigation,
              )
            : '';

    const poolInput: PoolPatientInput[] = patients.map((patient) => {
        if (!patient.personalData) {
            console.error("Patient without personal data", patient);
        }
        return {
            id: patient.id,
            uuid: patient.uuid,
            dateCreated: patient.dateCreated,
            status: patient.status,
            personalData: patient.personalData,
        };
    });

    // SHER-337 — the synchronous main-thread loop (decryptSinglePatientData) is
    // the CANONICAL, always-available implementation; the worker pool is a
    // best-effort accelerator. If the pool fails for ANY reason (worker
    // construction / bundling / CSP worker-src / a worker error), fall back to
    // decrypting this page synchronously — the exact code the app used before
    // this feature — so census load never breaks on the very tenant this
    // targets. Both paths produce identical row shapes.
    let decryptedRows: DecryptResponseRow[];
    try {
        decryptedRows = await getDecryptPool().decryptPatients(
            poolInput,
            investigation.personalFields,
            keyInvestigation,
            investigation.permissions,
        );
    } catch (err) {
        console.warn(
            "SHER-337: decrypt worker pool failed, falling back to synchronous main-thread decryption",
            err,
        );
        decryptedRows = patients.map((patient) => ({
            id: patient.id,
            uuid: patient.uuid,
            dateCreated: patient.dateCreated,
            status: patient.status,
            personalData: patient.personalData
                ? decryptSinglePatientData(patient.personalData, investigation)
                : null,
        }));
    }

    const patientsToSave: StoredPatientRow[] = decryptedRows.map((patient) => ({
        id: patient.id,
        uuid: patient.uuid,
        uuidInvestigation: investigation.uuid,
        dateCreated: new Date(patient.dateCreated),
        // Narrow, intentional cast: decryption yields a flat Record keyed by
        // field name; IPersonalData is the structured view the rest of the app
        // reads it as. Same value, just a stronger static type — widening
        // IPersonalData itself is out of scope here.
        personalData: patient.personalData as unknown as IPatient['personalData'],
        status: patient.status ?? 0,
    }));
    console.timeEnd("Decrypting Patient Data");
    console.time("Saving Bulk Patients");
    await db.patients.bulkPut(patientsToSave);
    console.timeEnd("Saving Bulk Patients");
}




export const getAllPatientsInvestigation = async (uuidInvestigation:string): Promise<IPatient[]> => {
    return await db.patients
        .where('uuidInvestigation')
        .equals(uuidInvestigation)
        .sortBy('dateCreated')
  };
// Save data to IndexedDB
export const savePatient = async (patientData: IPatient, uuidInvestigation: string): Promise<void> => {
    await db.patients.put({
        id: patientData.id,
        uuid: patientData.uuid,
        uuidInvestigation: uuidInvestigation,
        dateCreated: patientData.dateCreated,
        personalData: patientData.personalData,
        status: patientData.status ?? 0
    });
};

// Type for patient fetch function
export type FetchPatientFn = (uuidPatient: string) => Promise<IPatient | null>;

// Fetch data from IndexedDB - this is the default/real implementation
export const fetchPatient: FetchPatientFn = async (uuidPatient: string): Promise<IPatient | null> => {
    const patient = await db.patients.get(uuidPatient);
    return patient ? patient : null;
};

export const updatePatientPersonalData = async (uuidPatient: string, updatedPersonalData: Partial<IPatient>): Promise<void> => {
    const existingPatient = await db.patients.get(uuidPatient);
    if (!existingPatient) {
        throw new Error(`Patient with uuid ${uuidPatient} not found`);
    }
    
    const updatedPatient = {
        ...existingPatient.personalData,
        ...updatedPersonalData,
        uuid: uuidPatient // Ensure uuid doesn't get overwritten
    };
    
    await db.patients.put(updatedPatient);
};

export const findPatientByUUIDAndInvestigation = async (uuid: string, uuidInvestigation: string): Promise<IPatient | null> => {
    const patient = await db.patients
        .where('uuidInvestigation')
        .equals(uuidInvestigation)
        .and(patient => patient.uuid === uuid)
        .first();
    return patient ? patient : null;
};

export const findPatientsByNameOrSurname = async (nameOrSurname: string, uuidInvestigation: string): Promise<IPatient[]> => {
    const searchTerms = nameOrSurname.toLowerCase().split(' ');
    return await db.patients
        .where('uuidInvestigation').equals(uuidInvestigation)
        .and(patient => {
            const fullName = `${patient.personalData.name} ${patient.personalData.surnames}`.toLowerCase();
            const matchhealthId = patient.personalData.automated_health_id ? patient.personalData.automated_health_id.toLowerCase().includes(nameOrSurname.toLowerCase()) : false;
            return searchTerms.every(term => fullName.includes(term)) || matchhealthId;
        })
        .toArray();
};

export async function clearPatientsStore(): Promise<void> {
    await db.patients.clear();
    // Clear event-based sync state from localStorage
    // Note: Sync functions now live in src/utils/index.jsx
    localStorage.removeItem('patient_sync_state');
    localStorage.removeItem('lastEventId');
    localStorage.removeItem('lastSyncTimestamp');
    localStorage.removeItem('lastSyncedPatientCount');
    localStorage.removeItem("lastUpdatePatients"); // Legacy timestamp-based sync
    console.log('🗑️ Cleared all patient data and sync state');
}
